import { ActivatedRoute, ActivatedRouteSnapshot, CanDeactivateFn, NavigationEnd, Router, Routes } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

/**
 * Where a collection step (Catalogs, Coupons, Promotions) says its form should be.
 *
 * Each of those steps is a list with an add/edit form drawn over it, and the form used to live
 * only in memory — a refresh, a bookmark or a shared link all landed back on the list, and the
 * Manage Campaigns list had to ask for a record's form through a throwaway `?edit=<rowId>`. Now
 * the form has an address of its own under the step:
 *
 *     …/campaigns/15/promotions               the list
 *     …/campaigns/15/promotions/new           the create form
 *     …/campaigns/15/promotions/edit/<rowId>  the edit form for one draft row
 *
 * The three are **componentless** children of the step's route (see {@link childFormRoutes}), so
 * moving between them changes the child route only: the step component, the form inside it and
 * the wizard shell — with every unsaved edit it is holding for the other steps — stay mounted.
 */
export type ChildFormRoute =
  | { mode: 'list' }
  | { mode: 'new'; source?: ChildSourceRoute }
  | { mode: 'edit'; rowId: string; source?: ChildSourceRoute };

/**
 * A form opened *over* a child form — a goal's "Create New Promotion" (its source promotion).
 * It rides in the same URL as the form beneath it, after a `source` segment:
 *
 *     …/promotions/edit/<goalRowId>/source/new?sourceTemplate=<key>
 *     …/promotions/edit/<goalRowId>/source/edit/<sourceRowId>
 *     …/promotions/new/source/new?template=goal&sourceTemplate=<key>
 *
 * so browser Back steps out of just the source promotion, onto the goal still holding its edits.
 */
export type ChildSourceRoute =
  | { mode: 'new' }
  | { mode: 'edit'; rowId: string };

/** The `data` key the child-form routes carry their mode under. */
export const CHILD_FORM_DATA_KEY = 'childForm';

/** The `data` key a route carries its nested source form's mode under (absent when it has none). */
export const CHILD_SOURCE_DATA_KEY = 'childSource';

/**
 * The list / new / edit children of a collection step. `stepId` is repeated on each because the
 * shell names the active step from the *deepest* activated route's data.
 *
 * `children: []` is what makes a route componentless without a redirect: nothing renders for it
 * (the step has no `<router-outlet>`), and its parent — the step — is reused across all three
 * rather than destroyed and rebuilt, which would drop the open form and everything typed in it.
 */
export function childFormRoutes(
  stepId: string,
  canDeactivate: CanDeactivateFn<unknown>[],
  options: { withSource?: boolean } = {}
): Routes {
  // Each is ONE route with a multi-segment path, not a route nested in another: the ids all land
  // in one `params` bag, and the step still sees a single componentless child to read. Listed
  // before `new` / `edit/:rowId` so those are not tried (and abandoned) first.
  const sourceRoutes: Routes = options.withSource
    ? [
        { path: 'new/source/new', data: { stepId, [CHILD_FORM_DATA_KEY]: 'new', [CHILD_SOURCE_DATA_KEY]: 'new' }, canDeactivate, children: [] },
        { path: 'new/source/edit/:sourceRowId', data: { stepId, [CHILD_FORM_DATA_KEY]: 'new', [CHILD_SOURCE_DATA_KEY]: 'edit' }, canDeactivate, children: [] },
        { path: 'edit/:rowId/source/new', data: { stepId, [CHILD_FORM_DATA_KEY]: 'edit', [CHILD_SOURCE_DATA_KEY]: 'new' }, canDeactivate, children: [] },
        { path: 'edit/:rowId/source/edit/:sourceRowId', data: { stepId, [CHILD_FORM_DATA_KEY]: 'edit', [CHILD_SOURCE_DATA_KEY]: 'edit' }, canDeactivate, children: [] }
      ]
    : [];
  return [
    { path: '', pathMatch: 'full', data: { stepId, [CHILD_FORM_DATA_KEY]: 'list' }, children: [] },
    ...sourceRoutes,
    { path: 'new', data: { stepId, [CHILD_FORM_DATA_KEY]: 'new' }, canDeactivate, children: [] },
    { path: 'edit/:rowId', data: { stepId, [CHILD_FORM_DATA_KEY]: 'edit' }, canDeactivate, children: [] }
  ];
}

/** Read the child-form route off the snapshot that carries it (the deepest activated one). */
export function childFormRouteOf(snapshot: ActivatedRouteSnapshot | null | undefined): ChildFormRoute {
  const mode = snapshot?.data?.[CHILD_FORM_DATA_KEY];
  const sourceMode = snapshot?.data?.[CHILD_SOURCE_DATA_KEY];
  const sourceRowId = snapshot?.paramMap.get('sourceRowId');
  const source: ChildSourceRoute | undefined = sourceMode === 'new'
    ? { mode: 'new' }
    : sourceMode === 'edit' && sourceRowId ? { mode: 'edit', rowId: sourceRowId } : undefined;

  if (mode === 'new') {
    return source ? { mode: 'new', source } : { mode: 'new' };
  }
  const rowId = snapshot?.paramMap.get('rowId');
  if (mode === 'edit' && rowId) {
    return source ? { mode: 'edit', rowId, source } : { mode: 'edit', rowId };
  }
  return { mode: 'list' };
}

function sameSourceRoute(a: ChildSourceRoute | undefined, b: ChildSourceRoute | undefined): boolean {
  if (!a || !b) {
    return !a && !b;
  }
  return a.mode === b.mode && (a.mode !== 'edit' || a.rowId === (b as { rowId: string }).rowId);
}

export function sameChildFormRoute(a: ChildFormRoute, b: ChildFormRoute): boolean {
  if (a.mode === 'list' || b.mode === 'list') {
    return a.mode === b.mode;
  }
  if (a.mode !== b.mode || (a.mode === 'edit' && a.rowId !== (b as { rowId: string }).rowId)) {
    return false;
  }
  return sameSourceRoute(a.source, b.source);
}

/** The URL commands for a child-form route, appended to a step's own. */
export function childFormCommands(target: ChildFormRoute): string[] {
  if (target.mode === 'list') {
    return [];
  }
  const form = target.mode === 'new' ? ['new'] : ['edit', target.rowId];
  const source = target.source;
  if (!source) {
    return form;
  }
  return [...form, 'source', ...(source.mode === 'new' ? ['new'] : ['edit', source.rowId])];
}

/**
 * The child-form route under a step, now and after every navigation — for the step component,
 * whose own `ActivatedRoute` is the parent of the three.
 *
 * Emits on subscribe (a refresh or a deep link already names its form) and then once per change,
 * so the address-bar update that follows the step opening a form itself is not re-announced to
 * it as a request to open the same form again.
 */
export function childFormRoute$(route: ActivatedRoute, router: Router): Observable<ChildFormRoute> {
  return router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => childFormRouteOf(route.snapshot.firstChild)),
    distinctUntilChanged(sameChildFormRoute)
  );
}

/**
 * The campaign id carried by the wizard's `:campaignId` route segment — or `null` while the
 * campaign is still being created.
 *
 * The shell serves creating and editing from **one** route so that adopting a freshly minted id
 * is a parameter change rather than a teardown (see app.routes.ts). The cost of that is a
 * segment that reads `"new"` instead of being absent, and `"new"` is *truthy* — which is exactly
 * the shape of value that slips past a guard clause. `getCampaignIdForPanel` had one:
 *
 *     if (this.campaignId) { const parsed = parseInt(this.campaignId, 10); return isNaN(parsed) ? 0 : parsed; }
 *     return this.wizard.draftCampaignDatabaseId || 0;   // ← never reached for "new"
 *
 * It would answer `0` for a campaign that had just been minted, instead of falling through to
 * the id the wizard was holding. Every reader wants the same thing — "the campaign's id, or
 * nothing yet" — so they all ask here rather than each deciding what `"new"` means.
 */
export function campaignIdFromRoute(route: ActivatedRoute): string | null {
  const value = route.snapshot.paramMap.get('campaignId');
  return value === 'new' ? null : value;
}
